import { useRef, useState, useEffect, useMemo } from "react";
import cn from "classnames";
import { dateStringToDate, isFilledDate, isRangeMode } from "../utils";
import { EMPTY_DATE } from "../constants";
import { Days } from "./days";
import { Months } from "./months";
import { Years } from "./years";
import { PANELS } from "./constants";
import { CalendarProps, Panel } from "./types";
import {
  getViewDate,
  isValidDateValue,
  formatDateToLocaleString,
} from "./utils";
import styles from "./styles.module.scss";

export const Calendar = ({
  value,
  minValue,
  maxValue,
  className,
  datepickerRef,
  dayRefs,
  onSelectDate,
  onOutsideClick,
  dataTestId = "calendar",
}: CalendarProps) => {
  const calendarRef = useRef<HTMLDivElement>(null);
  const [panel, setPanel] = useState<Panel>(PANELS.DAYS);

  const minDate = dateStringToDate(minValue);
  const maxDate = dateStringToDate(maxValue);

  const computedViewDate = useMemo(
    () => getViewDate(value, minDate, maxDate),
    [value, minDate, maxDate]
  );

  const [viewDate, setViewDate] = useState(computedViewDate);

  const selectedDate = isRangeMode(value)
    ? value.map(dateStringToDate)
    : dateStringToDate(value);

  useEffect(() => {
    if (
      isValidDateValue(value, minDate, maxDate) &&
      computedViewDate !== viewDate
    ) {
      setViewDate(computedViewDate);
    }
    // в имеющейся реализации требуется только сайд эффект value
  }, [value]);

  useEffect(() => {
    const handleOutsideClick = (event: Event) => {
      const targetElement = event?.target as HTMLElement;
      const calendarElement = calendarRef?.current;
      const datepickerElement = datepickerRef?.current;

      const isOutsideClick = ![datepickerElement, calendarElement].some(
        (element) => element && element.contains(targetElement)
      );

      if (isOutsideClick) {
        onOutsideClick?.();
      }
    };

    window.addEventListener("mousedown", handleOutsideClick);

    return () => window.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const setDaysPanel = () => {
    setPanel(PANELS.DAYS);
  };

  const setMonthsPanel = () => {
    setPanel(PANELS.MONTHS);
  };

  const setYearsPanel = () => {
    setPanel(PANELS.YEARS);
  };

  const handleSelectDay = (day: number) => {
    const newDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    const newRuDateString = formatDateToLocaleString(newDate);

    if (isRangeMode(selectedDate)) {
      if (isFilledDate(value) || !isFilledDate(value, { isPartial: true })) {
        onSelectDate([newRuDateString, EMPTY_DATE]);
      } else {
        onSelectDate([
          formatDateToLocaleString(selectedDate[0]),
          newRuDateString,
        ]);
      }
    } else {
      onSelectDate(newRuDateString);
    }
  };

  const handleSelectMonth = (month: number) => {
    setViewDate(new Date(viewDate.getFullYear(), month));
    setDaysPanel();
  };

  const setPrevMonth = () => {
    handleSelectMonth(viewDate.getMonth() - 1);
  };

  const setNextMonth = () => {
    handleSelectMonth(viewDate.getMonth() + 1);
  };

  const handleSelectYear = (year: number) => {
    setViewDate(new Date(year, viewDate.getMonth()));
    setDaysPanel();
  };

  const getPanel = () => {
    switch (panel) {
      case PANELS.DAYS:
        return (
          <Days
            minDate={minDate}
            maxDate={maxDate}
            viewDate={viewDate}
            selectedDate={selectedDate}
            dayRefs={dayRefs}
            onSelectDay={handleSelectDay}
            showPrevMonth={setPrevMonth}
            showNextMonth={setNextMonth}
            showMonthsPanel={setMonthsPanel}
            showYearsPanel={setYearsPanel}
            dataTestId={`${dataTestId}-days`}
          />
        );

      case PANELS.MONTHS:
        return (
          <Months
            minDate={minDate}
            maxDate={maxDate}
            viewDate={viewDate}
            showStartPanel={setDaysPanel}
            onSelectMonth={handleSelectMonth}
            dataTestId={`${dataTestId}-months`}
          />
        );

      case PANELS.YEARS:
        return (
          <Years
            selectedYear={viewDate.getFullYear()}
            minYear={minDate?.getFullYear()}
            maxYear={maxDate?.getFullYear()}
            onSelectYear={handleSelectYear}
            showStartPanel={setDaysPanel}
            dataTestId={`${dataTestId}-years`}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div
      ref={calendarRef}
      className={cn(styles.calendar, className)}
      data-test-id={dataTestId}
    >
      {getPanel()}
    </div>
  );
};
